import { useState, useEffect } from "react";
import PublicLayout from "../components/PublicLayout";
import { Activity, Layers, Lock, Globe, KeyRound, Zap, Check, ShieldAlert, ShieldCheck } from "lucide-react";

const FEATURE_DETAILS = [
    {
        id: "subdomains",
        icon: Activity,
        title: "Persistent Subdomains",
        tagline: "Your URL never changes. No more rotating random links.",
        desc: "SynapseLink allows you to lock down your subdomain once (e.g., `my-project.synapselink.cloud`). It remains assigned to your account and activates instantly whenever you start tunneling. Stop updating webhook URLs in Stripe, Twilio, or GitHub every time you restart your local server."
    },
    {
        id: "inspector",
        icon: Layers,
        title: "Live Request Inspector",
        tagline: "Inspect full request payloads inside your web browser.",
        desc: "Watch requests hit your local port in real time. The built-in inspector records incoming headers, query parameters, cookies, and raw JSON request/response bodies. You can expand, format, and even replay webhook events with a single click."
    },
    {
        id: "auth",
        icon: Lock,
        title: "Password Protection",
        tagline: "Put your dev environment behind a secure gateway.",
        desc: "Working on a private client build or staging API? Enable basic authentication on any tunnel instantly from the web dashboard. Visitors are prompted with a secure passcode screen before any traffic is forwarded to your machine."
    },
    {
        id: "domains",
        icon: Globe,
        title: "Custom Domains",
        tagline: "Run localhost tunnels on your own company domain.",
        desc: "Promote staging builds or run webhooks on domains like `api.yourcompany.com`. Simply configure a CNAME record pointing to our relay, click verify, and let SynapseLink automatically handle SSL certificate provisioning and routing."
    },
    {
        id: "keys",
        icon: KeyRound,
        title: "CLI & API Keys",
        tagline: "Secure, machine-friendly authentication.",
        desc: "Connect your CLI without oauth popup loops. Generate long-lived, scoped API keys from your dashboard. Revoke keys instantly, rotate credentials, or authorize headless servers and CI/CD pipelines to tunnel automatically."
    },
    {
        id: "relay",
        icon: Zap,
        title: "Rate-limited Relay",
        tagline: "DDoS protection and traffic shielding at the edge.",
        desc: "Our global edge nodes automatically throttle abusive traffic, bots, and brute force requests before they reach your local port. Protect your development workstation from crashes and resource exhaustion."
    }
];

const FRAMEWORK_GUIDES = [
    { title: "React & Next.js", desc: "Expose React localhost or Next.js localhost frontend servers (typically port 3000 or 5173). Perform localhost https testing of OAuth loops and responsive page layouts.", cmd: "synapselink 3000" },
    { title: "Vue & Angular", desc: "Expose Vue localhost or Angular localhost dev servers (ports 5173 or 4200) to clients or mobile browsers for rapid design iteration.", cmd: "synapselink 5173" },
    { title: "Express & NestJS", desc: "Connect webhooks to a localhost tunnel for Express or NestJS applications. Stream incoming JSON request payloads live inside your web browser.", cmd: "synapselink 3000" },
    { title: "FastAPI & Go", desc: "Spin up a high-performance localhost tunnel for FastAPI or Go applications. Test REST endpoints or gRPC streams remotely.", cmd: "synapselink 8000" },
    { title: "Spring Boot & Java", desc: "Deploy a secure localhost tunnel for Spring Boot. Expose local servers online securely with dynamic port mappings.", cmd: "synapselink 8080" },
    { title: "ASP.NET & IIS", desc: "Establish a secure localhost tunnel for ASP.NET web apps. Binds safely to default Windows IIS Express or custom HTTPS ports.", cmd: "synapselink 5001" }
];

const MOCK_REQUESTS = [
    { id: 1, method: "POST", path: "/v1/webhooks/stripe", status: 200, time: "14:32:01", duration: "124ms", type: "webhook", body: '{ "id": "evt_1Op4b8L", "type": "payment_intent.succeeded", "amount": 9900 }' },
    { id: 2, method: "GET", path: "/api/users/me?include=profile", status: 200, time: "14:32:03", duration: "48ms", type: "api", body: '{ "user": "operator_41", "verified": true }' },
    { id: 3, method: "POST", path: "/api/auth/login", status: 401, time: "14:32:05", duration: "185ms", type: "auth", body: '{ "error": "Invalid credential token hash" }' },
    { id: 4, method: "PUT", path: "/v2/customers/cust_99a", status: 200, time: "14:32:08", duration: "92ms", type: "api", body: '{ "status": "active", "tier": "pro_tier" }' },
    { id: 5, method: "GET", path: "/static/assets/logo.png", status: 304, time: "14:32:10", duration: "14ms", type: "static", body: "[ binary stream / payload ]" }
];

export default function FeaturesPage() {
    const [selectedFeature, setSelectedFeature] = useState("inspector");

    useEffect(() => {
        document.title = "SynapseLink Features - Expose Localhost to the Internet Securely";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute("content", "Expose React, Next.js, Express, Go, and FastAPI local server ports online. Create secure localhost tunnels without port forwarding to share with clients.");
        }
    }, []);
    
    // Request Inspector simulation states
    const [inspectedLogs, setInspectedLogs] = useState(MOCK_REQUESTS.slice(0, 3));
    const [selectedLog, setSelectedLog] = useState(MOCK_REQUESTS[0]);
    const [nextLogIndex, setNextLogIndex] = useState(3);

    // Password Lock state
    const [isPasswordLocked, setIsPasswordLocked] = useState(false);

    // Simulated CLI copy state
    const [copiedKey, setCopiedKey] = useState(false);

    // Simulate incoming traffic in the inspector
    useEffect(() => {
        const interval = setInterval(() => {
            const nextLog = MOCK_REQUESTS[nextLogIndex];
            setInspectedLogs((prev) => {
                const updated = [nextLog, ...prev];
                if (updated.length > 5) updated.pop();
                return updated;
            });
            setSelectedLog(nextLog);
            setNextLogIndex((prev) => (prev + 1) % MOCK_REQUESTS.length);
        }, 3000);

        return () => clearInterval(interval);
    }, [nextLogIndex]);

    const handleCopyKey = () => {
        navigator.clipboard.writeText("sl_live_83d29bb084fc11adbc7e2501a14c");
        setCopiedKey(true);
        setTimeout(() => setCopiedKey(false), 2000);
    };

    return (
        <PublicLayout>
            <div className="max-w-[1400px] mx-auto px-6 py-16">
                
                {/* Hero Section */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="label-mono text-[#00FF41] mb-3">// core capabilities</div>
                    <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight mb-6 uppercase">
                        EXPOSING LOCALHOST <br />
                        WITH ENTERPRISE RIGOR
                    </h1>
                    <p className="text-[#A1A1AA] text-sm md:text-base leading-relaxed">
                        We did not just copy existing tunnel clients. We designed a high-throughput, secure, and configurable relay architecture built directly for modern development teams.
                    </p>
                </div>

                {/* Features Tabs and Interactive Demos */}
                <div className="grid lg:grid-cols-12 gap-0 border border-[#27272A] bg-[#0A0A0A] mb-20">
                    
                    {/* Left: Tab Selectors */}
                    <div className="lg:col-span-5 border-r border-[#27272A] divide-y divide-[#27272A]">
                        {FEATURE_DETAILS.map((f) => {
                            const Icon = f.icon;
                            const isSelected = selectedFeature === f.id;
                            return (
                                <button
                                    key={f.id}
                                    onClick={() => setSelectedFeature(f.id)}
                                    className={`w-full text-left p-6 font-mono transition-all duration-150 flex items-start gap-4 ${isSelected ? "bg-[#0F0F0F] border-l-4 border-l-[#00FF41]" : "hover:bg-[#050505]/40"}`}
                                >
                                    <Icon className={`w-6 h-6 shrink-0 ${isSelected ? "text-[#00FF41]" : "text-[#52525B]"}`} strokeWidth={1.5} />
                                    <div>
                                        <h3 className={`font-bold text-sm uppercase tracking-wider ${isSelected ? "text-[#FAFAFA]" : "text-[#A1A1AA]"}`}>{f.title}</h3>
                                        <p className="text-xs text-[#52525B] mt-1 font-mono">{f.tagline}</p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Right: Custom Interactive Demonstrators */}
                    <div className="lg:col-span-7 p-8 flex flex-col justify-between min-h-[460px]">
                        
                        {/* Tab Details */}
                        <div className="mb-8">
                            <div className="label-mono text-[#00FF41] mb-2">// interactive simulator</div>
                            <h2 className="heading-md mb-3 text-[#FAFAFA]">
                                {FEATURE_DETAILS.find((f) => f.id === selectedFeature)?.title}
                            </h2>
                            <p className="text-xs md:text-sm text-[#A1A1AA] leading-relaxed">
                                {FEATURE_DETAILS.find((f) => f.id === selectedFeature)?.desc}
                            </p>
                        </div>

                        {/* Interactive Demonstrator Area */}
                        <div className="border border-[#27272A] bg-[#050505] p-5 rounded-none font-mono relative overflow-hidden flex-grow flex flex-col justify-center min-h-[260px]">
                            
                            {/* 1. Subdomain Demo */}
                            {selectedFeature === "subdomains" && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between border-b border-[#18181B] pb-2">
                                        <span className="text-[10px] text-[#52525B]">// RESERVING SUBDOMAIN</span>
                                        <span className="text-[10px] text-[#00FF41] uppercase">status: active</span>
                                    </div>
                                    <div className="text-xs text-[#FAFAFA]">
                                        <span className="text-[#00FF41]">$</span> synapselink 8000 --subdomain my-awesome-app
                                    </div>
                                    <pre className="text-[10px] leading-relaxed text-[#A1A1AA] bg-[#0A0A0A] p-3 border border-[#27272A]">
{`▸ Initializing secure relay tunnel...
▸ Checking subdomain availability...
✔ Subdomain reserved successfully!
▸ Proxy active: http://localhost:8000
              ↳ https://my-awesome-app.synapselink.cloud`}
                                    </pre>
                                </div>
                            )}

                            {/* 2. Live Request Inspector Demo */}
                            {selectedFeature === "inspector" && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
                                    {/* Scroll Feed */}
                                    <div className="border border-[#27272A] divide-y divide-[#18181B] max-h-[190px] overflow-y-auto">
                                        <div className="bg-[#0A0A0A] text-[9px] px-2 py-1 border-b border-[#27272A] text-[#52525B] font-bold sticky top-0 flex justify-between">
                                            <span>LIVE REQUESTS</span>
                                            <span className="crt-flicker text-[#00FF41]">● POLLING</span>
                                        </div>
                                        {inspectedLogs.map((log) => (
                                            <button
                                                key={log.id}
                                                onClick={() => setSelectedLog(log)}
                                                className={`w-full text-left p-2 flex items-center justify-between text-[10px] transition-colors ${selectedLog.id === log.id ? "bg-[#0F0F0F] border-l-2 border-l-[#00FF41]" : "hover:bg-[#0A0A0A]"}`}
                                            >
                                                <span className={`font-bold ${log.method === "POST" ? "text-[#FF9900]" : "text-[#00FF41]"}`}>{log.method}</span>
                                                <span className="text-[#FAFAFA] truncate max-w-[80px]">{log.path}</span>
                                                <span className={log.status === 200 ? "text-[#00FF41]" : "text-[#FF3B30]"}>{log.status}</span>
                                            </button>
                                        ))}
                                    </div>

                                    {/* Detail Panel */}
                                    <div className="border border-[#27272A] bg-[#0A0A0A] p-2 flex flex-col justify-between text-[9px] max-h-[190px]">
                                        <div>
                                            <div className="text-[#52525B] border-b border-[#18181B] pb-1 mb-1 font-bold">PAYLOAD DETAIL</div>
                                            <div className="text-[#FAFAFA]"><span className="text-[#52525B]">PATH:</span> {selectedLog.path}</div>
                                            <div className="text-[#FAFAFA]"><span className="text-[#52525B]">TIME:</span> {selectedLog.time} · {selectedLog.duration}</div>
                                        </div>
                                        <pre className="text-[#00FF41] bg-[#050505] p-1.5 border border-[#27272A] overflow-x-auto text-[8px] max-h-[100px] mt-1">
                                            {selectedLog.body}
                                        </pre>
                                    </div>
                                </div>
                            )}

                            {/* 3. Password Protect Demo */}
                            {selectedFeature === "auth" && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs uppercase text-[#A1A1AA]">Dashboard Password Toggle</span>
                                        <button 
                                            onClick={() => setIsPasswordLocked(!isPasswordLocked)}
                                            className={`tf-btn !py-1 !px-3 !text-[10px] ${isPasswordLocked ? "!bg-[#FF3B30] !border-[#FF3B30] text-white hover:!shadow-red-500" : ""}`}
                                        >
                                            {isPasswordLocked ? "DISABLE PROTECTION" : "ENABLE GATE"}
                                        </button>
                                    </div>

                                    <div className={`p-4 border transition-all duration-300 ${isPasswordLocked ? "bg-[#180C0B] border-[#FF3B30]" : "bg-[#0A0A0A] border-[#27272A]"}`}>
                                        <div className="flex items-center gap-3">
                                            {isPasswordLocked ? (
                                                <ShieldAlert className="w-8 h-8 text-[#FF3B30] shrink-0" />
                                            ) : (
                                                <ShieldCheck className="w-8 h-8 text-[#00FF41] shrink-0" />
                                            )}
                                            <div>
                                                <div className="text-xs font-bold uppercase">{isPasswordLocked ? "BASIC AUTH ACTIVE (HTTP 401)" : "TUNNEL IS PUBLIC (HTTP 200)"}</div>
                                                <p className="text-[10px] text-[#A1A1AA] mt-0.5">
                                                    {isPasswordLocked 
                                                        ? "Incoming connections will be prompted to supply your passcode." 
                                                        : "All internet traffic is forwarded directly to your port."
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 4. Custom Domains Demo */}
                            {selectedFeature === "domains" && (
                                <div className="space-y-3">
                                    <div className="text-[10px] text-[#52525B]">// DNS CONFIGURATION TABLE</div>
                                    <table className="w-full text-left text-[10px] border border-[#27272A]">
                                        <thead>
                                            <tr className="bg-[#0A0A0A] border-b border-[#27272A] text-[#A1A1AA]">
                                                <th className="p-2">TYPE</th>
                                                <th className="p-2">HOST</th>
                                                <th className="p-2">TARGET VALUE</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-[#27272A]">
                                            <tr>
                                                <td className="p-2 text-[#00E5FF]">CNAME</td>
                                                <td className="p-2 text-[#FAFAFA]">api</td>
                                                <td className="p-2 text-[#00FF41] font-bold">cname.synapselink.cloud</td>
                                            </tr>
                                            <tr>
                                                <td className="p-2 text-[#00E5FF]">CNAME</td>
                                                <td className="p-2 text-[#FAFAFA]">staging</td>
                                                <td className="p-2 text-[#00FF41] font-bold">cname.synapselink.cloud</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div className="text-[9px] text-[#52525B]">SSL certification is requested and renewed automatically at the edge.</div>
                                </div>
                            )}

                            {/* 5. API Keys Demo */}
                            {selectedFeature === "keys" && (
                                <div className="space-y-3">
                                    <div className="text-xs uppercase text-[#A1A1AA] flex justify-between items-center border-b border-[#18181B] pb-2">
                                        <span>Authentication Key</span>
                                        <span className="text-[10px] text-[#52525B]">sl_live_...a14c</span>
                                    </div>
                                    <p className="text-[10px] text-[#A1A1AA]">Generate live keys to authenticate CI pipelines and remote machines without browser redirects.</p>
                                    <div className="flex gap-2">
                                        <div className="bg-[#0A0A0A] border border-[#27272A] px-3 py-1.5 font-mono text-[#00FF41] text-xs flex-1 truncate">
                                            sl_live_83d29bb084fc11adbc7e2501a14c
                                        </div>
                                        <button 
                                            onClick={handleCopyKey}
                                            className="tf-btn !py-1.5 !px-3 text-xs"
                                        >
                                            {copiedKey ? "COPIED" : "COPY KEY"}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* 6. Rate-limiting Demo */}
                            {selectedFeature === "relay" && (
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center text-[10px] text-[#52525B] border-b border-[#18181B] pb-2">
                                        <span>EDGE GATEWAY STATE</span>
                                        <span className="text-[#00FF41] font-bold">PROTECTION: ON</span>
                                    </div>
                                    <div className="space-y-1">
                                        <div className="flex justify-between text-[10px]">
                                            <span className="text-[#FAFAFA]">IP 194.22.1.84</span>
                                            <span className="text-[#00FF41]">FORWARDED (200 OK)</span>
                                        </div>
                                        <div className="flex justify-between text-[10px]">
                                            <span className="text-[#FAFAFA]">IP 8.8.8.8</span>
                                            <span className="text-[#00FF41]">FORWARDED (200 OK)</span>
                                        </div>
                                        <div className="flex justify-between text-[10px] bg-[#180C0B] px-1">
                                            <span className="text-[#FF3B30]">IP 203.0.113.5 (Abusive Client)</span>
                                            <span className="text-[#FF3B30] font-bold">THROTTLED (429 RATE LIMIT)</span>
                                        </div>
                                    </div>
                                    <div className="label-mono text-[9px] text-center mt-2 text-[#52525B]">Shielding your workstation from traffic spikes.</div>
                                </div>
                            )}

                        </div>

                    </div>
                </div>

                {/* FRAMEWORK INTEGRATIONS */}
                <section className="mb-20">
                    <div className="label-mono text-[#00FF41] mb-3">// FRAMEWORK INTEGRATION GUIDES</div>
                    <h2 className="heading-md mb-4 text-[#FAFAFA]">HOW TO EXPOSE FRAMEWORK LOCALHOST PORTS</h2>
                    <p className="text-xs md:text-sm text-[#A1A1AA] leading-relaxed max-w-3xl mb-8">
                        Expose React, Next.js, Vue, Angular, Express, NestJS, FastAPI, Spring Boot, ASP.NET, or Go application ports to a public URL. SynapseLink is the best free ngrok alternative, enabling localhost https testing, remote debugging, and client reviews with a single terminal command.
                    </p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#27272A] bg-[#0A0A0A]">
                        {FRAMEWORK_GUIDES.map((g, i) => (
                            <div key={i} className="p-6 border-r border-b border-[#27272A] last:border-r-0 [&:nth-child(3n)]:border-r-0 flex flex-col justify-between hover:bg-[#0F0F0F] transition-colors group">
                                <div>
                                    <h3 className="font-display text-sm font-bold text-[#FAFAFA] mb-2 uppercase group-hover:text-[#00FF41] transition-colors">{g.title}</h3>
                                    <p className="text-[11px] text-[#A1A1AA] leading-relaxed mb-4">{g.desc}</p>
                                </div>
                                <div className="bg-[#050505] border border-[#27272A] p-2.5 font-mono text-[10px] text-[#00FF41] flex justify-between items-center">
                                    <code>$ {g.cmd}</code>
                                    <span className="text-[#52525B] text-[8px] uppercase">CLI command</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* TECHNICAL DEEP DIVE */}
                <section className="grid lg:grid-cols-2 gap-10 mb-20">
                    <div className="space-y-6">
                        <div className="label-mono text-[#00FF41]">// SEAMLESS REMOTE ACCESS</div>
                        <h2 className="heading-md text-[#FAFAFA]">How to Expose Localhost to the Internet & Share with Clients</h2>
                        <p className="text-xs md:text-sm text-[#A1A1AA] leading-relaxed">
                            When reviewing changes with clients or stakeholders, uploading draft builds to staging servers is slow and tedious. 
                            With SynapseLink, you can <strong>access localhost remotely</strong> and <strong>share localhost with clients</strong> in real time. 
                            Simply start the tunnel proxy on your workstation, copy the persistent public HTTPS URL, and share it instantly. 
                            Clients can inspect the live site as it runs directly on your machine.
                        </p>
                        <p className="text-xs md:text-sm text-[#A1A1AA] leading-relaxed">
                            Need to put your client preview behind a lock? Toggle password protection in your dashboard without restarting 
                            your local compiler or modifying configuration variables. It is a fully <strong>secure localhost tunnel</strong> 
                            bridged securely at the network edge.
                        </p>
                    </div>
                    <div className="space-y-6">
                        <div className="label-mono text-[#00FF41]">// ZERO PORT FORWARDING CONFIG</div>
                        <h3 className="heading-md text-[#FAFAFA]">Localhost Tunnel without Port Forwarding or DNS Hassles</h3>
                        <p className="text-xs md:text-sm text-[#A1A1AA] leading-relaxed">
                            Setting up standard routing rules on home or enterprise routers requires administrator access, static IP setup, 
                            and exposes your local IP network directly to the public web. SynapseLink creates a secure egress multiplexed bridge, 
                            giving you a <strong>localhost tunnel without port forwarding</strong>.
                        </p>
                        <p className="text-xs md:text-sm text-[#A1A1AA] leading-relaxed">
                            We automatically provision custom SSL/TLS certificates to <strong>create https tunnel for localhost</strong> 
                            testing. This allows you to <strong>expose local server online</strong> with absolute confidence. It handles 
                            responsive localhost https testing, API calls, and webhooks under full HTTP/2 encryption.
                        </p>
                    </div>
                </section>

                {/* Developer Grid CTA */}
                <div className="border border-[#27272A] bg-[#0A0A0A] p-10 text-center relative overflow-hidden">
                    <div className="absolute inset-0 pointer-events-none opacity-[0.02] bg-[radial-gradient(#00FF41_1px,transparent_1px)]" style={{ backgroundSize: "16px 16px" }} />
                    <h2 className="heading-md mb-4 text-[#FAFAFA]">DEPLOY SECURE PIPELINES IN SECONDS</h2>
                    <p className="text-sm text-[#A1A1AA] max-w-xl mx-auto mb-6">
                        Claim your persistent subdomain on our free tier, install the CLI installer package, and sync local projects seamlessly.
                    </p>
                    <a href="/register" className="tf-btn">
                        GET STARTED FOR FREE
                    </a>
                </div>

            </div>
        </PublicLayout>
    );
}
